import React, { useState, useEffect, useCallback } from 'react';

const WorkflowRunner = ({ workflow, onClose }) => {
  const [currentNode, setCurrentNode] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [responses, setResponses] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const startSession = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/workflows/${workflow.id}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const session = await response.json();
      setSessionId(session.id);
      
      // Find the start node
      const startNode = workflow.nodes.find(n => n.id === session.current_node_id);
      setCurrentNode(startNode);
      setHistory([startNode]);
    } catch (error) {
      console.error('Error starting session:', error);
    } finally {
      setLoading(false);
    }
  }, [workflow?.id, workflow?.nodes, backendUrl]);

  useEffect(() => {
    if (workflow) {
      startSession();
    }
  }, [workflow, startSession]);

  const navigateToNode = async (nodeId, userResponse = null) => {
    try {
      setLoading(true);
      
      // Find the target node
      const targetNode = workflow.nodes.find(n => n.id === nodeId);
      if (!targetNode) {
        console.error('Target node not found:', nodeId);
        return;
      }

      // Record the response if provided
      if (userResponse && sessionId) {
        await fetch(`${backendUrl}/api/workflows/sessions/${sessionId}/navigate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            node_id: currentNode.id,
            response: userResponse,
            next_node_id: nodeId,
            completed: targetNode.node_type === 'end'
          }),
        });
      }

      // Update current node
      setCurrentNode(targetNode);
      
      // Add to history if not already there (for loops)
      if (!history.find(h => h.id === targetNode.id)) {
        setHistory([...history, targetNode]);
      }
      
      // Add response to local state
      if (userResponse) {
        setResponses([...responses, { node: currentNode, response: userResponse }]);
      }
      
      // Check if workflow is completed
      if (targetNode.node_type === 'end') {
        setCompleted(true);
      }
    } catch (error) {
      console.error('Error navigating to node:', error);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (history.length > 1) {
      const previousNode = history[history.length - 2];
      setCurrentNode(previousNode);
      setHistory(history.slice(0, -1));
      setResponses(responses.slice(0, -1));
    }
  };

  const restart = () => {
    setCurrentNode(null);
    setHistory([]);
    setResponses([]);
    setCompleted(false);
    startSession();
  };

  const NodeDisplay = ({ node }) => {
    if (!node) return null;

    const getNodeColor = () => {
      switch (node.node_type) {
        case 'start': return 'bg-green-500';
        case 'decision': return 'bg-blue-500';
        case 'action': return 'bg-purple-500';
        case 'end': return 'bg-red-500';
        default: return 'bg-gray-500';
      }
    };

    const getConnectedNodes = () => {
      return node.connections?.map(connectionId => 
        workflow.nodes.find(n => n.id === connectionId)
      ).filter(Boolean) || [];
    };

    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">{node.icon}</div>
          <h2 className="text-3xl font-bold text-white mb-4">{node.title}</h2>
          <p className="text-xl text-slate-300 leading-relaxed mb-6">{node.description}</p>
        </div>

        <div className={`${getNodeColor()} rounded-2xl p-8 mb-8 shadow-2xl`}>
          <div className="text-center text-white">
            <div className="text-sm font-medium mb-2 opacity-90">
              {node.node_type.charAt(0).toUpperCase() + node.node_type.slice(1)} Node
            </div>
            
            {node.node_type === 'decision' && node.options && (
              <div className="space-y-4">
                <p className="text-lg mb-6">Choose an option to continue:</p>
                <div className="grid gap-4">
                  {node.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        // Find the connected node for this option
                        const connectedNodes = getConnectedNodes();
                        const targetNode = connectedNodes[index] || connectedNodes[0];
                        if (targetNode) {
                          navigateToNode(targetNode.id, option);
                        }
                      }}
                      className="w-full p-4 bg-white/20 hover:bg-white/30 rounded-xl text-left transition-colors duration-200 backdrop-blur-sm"
                      disabled={loading}
                    >
                      <div className="text-lg font-medium">{option.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {node.node_type === 'action' && (
              <div className="space-y-4">
                <p className="text-lg mb-6">Action completed. Continue to next step:</p>
                <div className="grid gap-4">
                  {getConnectedNodes().map((connectedNode, index) => (
                    <button
                      key={index}
                      onClick={() => navigateToNode(connectedNode.id)}
                      className="w-full p-4 bg-white/20 hover:bg-white/30 rounded-xl text-left transition-colors duration-200 backdrop-blur-sm"
                      disabled={loading}
                    >
                      <div className="text-lg font-medium">Continue →</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {node.node_type === 'start' && (
              <div className="space-y-4">
                <p className="text-lg mb-6">Welcome! Let's get started:</p>
                <div className="grid gap-4">
                  {getConnectedNodes().map((connectedNode, index) => (
                    <button
                      key={index}
                      onClick={() => navigateToNode(connectedNode.id)}
                      className="w-full p-4 bg-white/20 hover:bg-white/30 rounded-xl text-left transition-colors duration-200 backdrop-blur-sm"
                      disabled={loading}
                    >
                      <div className="text-lg font-medium">Start →</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {node.node_type === 'end' && (
              <div className="space-y-4">
                <p className="text-lg mb-6">Workflow completed!</p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={restart}
                    className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors duration-200 backdrop-blur-sm"
                  >
                    Start Over
                  </button>
                  <button
                    onClick={onClose}
                    className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors duration-200 backdrop-blur-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ProgressBar = () => {
    const progressPercent = workflow.nodes.length > 0 ? (history.length / workflow.nodes.length) * 100 : 0;
    
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">Progress</span>
          <span className="text-sm text-slate-400">{Math.round(progressPercent)}%</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const ResponseHistory = () => {
    if (responses.length === 0) return null;
    
    return (
      <div className="mt-8 bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Your Responses</h3>
        <div className="space-y-3">
          {responses.map((item, index) => (
            <div key={index} className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg">
              <span className="text-2xl">{item.node.icon}</span>
              <div className="flex-1">
                <div className="text-sm text-slate-300">{item.node.title}</div>
                <div className="text-xs text-slate-500">{item.response.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading && !currentNode) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">{workflow.title}</h1>
            <p className="text-slate-400">{workflow.description}</p>
          </div>
          <div className="flex items-center gap-4">
            {history.length > 1 && !completed && (
              <button
                onClick={goBack}
                className="px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"
              >
                ← Back
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        <ProgressBar />

        {/* Current Node */}
        {currentNode && (
          <NodeDisplay node={currentNode} />
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-slate-800 rounded-xl p-6 flex items-center gap-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <span className="text-white">Processing...</span>
            </div>
          </div>
        )}

        {/* Completion Celebration */}
        {completed && (
          <div className="text-center mt-8">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-white mb-4">Workflow Complete!</h2>
            <p className="text-xl text-slate-300 mb-6">
              You've successfully navigated through the entire workflow.
            </p>
          </div>
        )}

        <ResponseHistory />
      </div>
    </div>
  );
};

export default WorkflowRunner;